import React, { useEffect, useCallback, useState, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Modal } from '@mui/material';
import {
  fetchProjectUsers as fetchProjectUsersAction,
  setOpenPopup,
  clear,
  changePage as changePageAction,
  setFilter as setFilterAction
} from '#action/project/users-popup';
import {
  Content,
  HeaderActions,
  HeaderTitle,
  ModalBox,
  PaginationContainer,
  PaginationCount,
  StyledCloseIcon,
  StyledHeader,
  StyledPagination,
  StyledTitle,
  SubTitle
} from './styles';
import TableView from './table';
import DataFilter from './components/filter/filter';

const UserPopup = ({
  open,
  project: propsProject,
  handleClose,
  customComponents = {},
  customActions = {},
  disabled
}) => {
  const dispatch = useDispatch();

  const {
    users,
    page,
    pages,
    itemsPerPage,
    totalCount,
    status,
    filter
  } = useSelector(s => s.projectUsersPopup, shallowEqual);

  const loading = ['idle', 'loading'].includes(status);
  const renderRef = useRef();
  const [selected, setSelected] = useState([]);
  const title = propsProject?.title;

  // Pagination
  const from = (itemsPerPage * page) - itemsPerPage + 1;
  const to = (itemsPerPage * page) > totalCount ? totalCount : itemsPerPage * page;

  const components = {
    DataFilter: customComponents.DataFilter ?? DataFilter
  };

  const { DataFilter: Filter } = components;

  const actions = {
    fetchProjectUsers: customActions.fetchProjectUsers ?? fetchProjectUsersAction,
    setFilter: customActions.setFilter ?? setFilterAction,
    changePage: customActions.changePage ?? changePageAction
  };

  const {
    fetchProjectUsers,
    setFilter,
    changePage
  } = actions;

  const fetchData = useCallback(async () => {
    await dispatch(fetchProjectUsers(propsProject?.id));
  }, [dispatch, fetchProjectUsers, propsProject?.id]);

  // Only first time render.
  useEffect(() => {
    if (renderRef.current) return;

    fetchData();

    renderRef.current = true;
  }, [fetchData]);

  useEffect(() => {
    setSelected([]);
  }, [filter, page]);

  const handlePageChange = useCallback(async (event, newPage) => {
    if (page !== newPage) {
      dispatch(changePage(newPage, propsProject?.id));
    }
  }, [dispatch, changePage, page, propsProject]);

  const handleClosePopup = async () => {
    dispatch(setOpenPopup(!open));
    handleClose?.();

    if (disabled) return;

    dispatch(clear());
  };

  return (
    <Modal
      open={open}
      onClose={handleClosePopup}
      aria-labelledby="Users popup"
      data-loading={loading ? 'true' : 'false'}
    >
      <ModalBox>
        <StyledCloseIcon onClick={handleClosePopup} />
        <StyledHeader>
          <HeaderTitle>
            <StyledTitle variant="h4">
              Users
            </StyledTitle>
            <SubTitle component="h6">
              <span>{title}</span>
            </SubTitle>
          </HeaderTitle>
          <HeaderActions>
            <Filter
              projectId={propsProject?.id}
              // addMode={addMode}
              setFilter={setFilter}
              changePage={changePage}
            />
          </HeaderActions>
        </StyledHeader>
        <Content>
          <TableView
            data={users}
            loading={loading}
            dispatch={dispatch}
            projectId={propsProject?.id}
            selected={selected}
            setSelected={setSelected}
            showModeOnly={true}
          />
        </Content>
        <PaginationContainer>
          {(!!pages && Number(pages) > 1) && (
          <StyledPagination
            shape="rounded"
            color="secondary"
            count={pages}
            page={page}
            onChange={handlePageChange}
          />)}
          {(users?.length > 0 && totalCount) && !loading ?
            <PaginationCount>{`${from} - ${to} out of ${totalCount}`}</PaginationCount> : null}
        </PaginationContainer>
      </ModalBox>
    </Modal>
  );
};

export default UserPopup;
